import React, { useState, useEffect } from 'react';
import {
    Users,
    CreditCard,
    TrendingUp,
    MessageSquare,
    Clock,
    Activity,
    Mail,
    Smartphone
} from 'lucide-react';
import { analyticsAPI } from '../services/api';

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAdminStats = async () => {
            try {
                const response = await analyticsAPI.getAdminStats();
                setStats(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching admin stats:', err);
                setError(err.response?.data?.error || 'Failed to load admin statistics. Ensure you have admin privileges.');
            } finally {
                setLoading(false);
            }
        };

        fetchAdminStats();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-red-900/20 border border-red-500/50 rounded-xl m-6">
                <h3 className="text-lg font-medium text-red-500 mb-2">Access Denied</h3>
                <p className="text-red-400">{error}</p>
            </div>
        );
    }

    if (!stats) return null;

    // Helper functions for formatting
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US').format(num);
    };

    const formatResponseTime = (seconds) => {
        if (!seconds || seconds === 0) return '0s';
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        return `${minutes} min`;
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-gray-400 mt-1">Platform-wide overview and advanced metrics.</p>
            </div>

            {/* Financial Overview */}
            <h2 className="text-xl font-semibold text-white/90 mt-8 mb-4 border-b border-gray-800 pb-2">Financials</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Monthly Recurring Revenue"
                    value={formatCurrency(stats.mrr)}
                    icon={<CreditCard className="w-6 h-6 text-green-400" />}
                    trend="+12%" // Example static trend, ideally from API
                    trendUp={true}
                />
                <StatCard
                    title="Total Revenue (All Time)"
                    value={formatCurrency(stats.totalRevenue)}
                    icon={<TrendingUp className="w-6 h-6 text-blue-400" />}
                />
                <StatCard
                    title="ARPU (Active)"
                    value={formatCurrency(stats.arpu)}
                    icon={<Activity className="w-6 h-6 text-purple-400" />}
                />
                <StatCard
                    title="Paying Customers"
                    value={formatNumber(stats.payingCustomers)}
                    icon={<Users className="w-6 h-6 text-indigo-400" />}
                    subtitle={`Conversion: ${stats.conversionRate}%`}
                />
            </div>

            {/* User Base Overview */}
            <h2 className="text-xl font-semibold text-white/90 mt-8 mb-4 border-b border-gray-800 pb-2">User Base</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Total Users"
                    value={formatNumber(stats.totalUsers)}
                    icon={<Users className="w-6 h-6 text-blue-400" />}
                />
                <StatCard
                    title="Recent Signups (7d)"
                    value={formatNumber(stats.recentSignups)}
                    icon={<TrendingUp className="w-6 h-6 text-green-400" />}
                />
                <StatCard
                    title="Active Trials"
                    value={formatNumber(stats.activeTrials)}
                    icon={<Activity className="w-6 h-6 text-yellow-400" />}
                />
                <StatCard
                    title="Churned Users"
                    value={formatNumber(stats.churnedUsers)}
                    icon={<Users className="w-6 h-6 text-red-400" />}
                    trendDown={true}
                />
            </div>

            {/* Usage Metrics */}
            <h2 className="text-xl font-semibold text-white/90 mt-8 mb-4 border-b border-gray-800 pb-2">Platform Usage</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                    title="Total Leads Processed"
                    value={formatNumber(stats.totalLeads)}
                    icon={<Users className="w-6 h-6 text-indigo-400" />}
                />
                <StatCard
                    title="Global Avg Response Time"
                    value={formatResponseTime(stats.avgResponseTimeSeconds)}
                    icon={<Clock className="w-6 h-6 text-orange-400" />}
                />
                <StatCard
                    title="Messages Sent"
                    value={formatNumber(stats.totalMessages)}
                    icon={<MessageSquare className="w-6 h-6 text-blue-400" />}
                    subtitle={`${stats.totalTemplates} Custom Templates`}
                />
            </div>

            {/* Integrations Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-indigo-400" />
                        Connected Channels
                    </h3>
                    <div className="space-y-4">
                        {Object.entries(stats.channelConnections || {}).map(([channel, count]) => (
                            <div key={channel} className="flex justify-between items-center">
                                <span className="capitalize text-gray-300">{channel}</span>
                                <span className="text-white font-medium bg-gray-700/50 px-3 py-1 rounded-full">{count}</span>
                            </div>
                        ))}
                        {Object.keys(stats.channelConnections || {}).length === 0 && (
                            <p className="text-sm text-gray-400 text-center py-4">No active channels yet.</p>
                        )}
                    </div>
                </div>

                <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                        <Mail className="w-5 h-5 text-blue-400" />
                        Active Lead Sources
                    </h3>
                    <div className="space-y-4">
                        {Object.entries(stats.activeLeadSources || {}).map(([source, count]) => (
                            <div key={source} className="flex justify-between items-center">
                                <span className="text-gray-300">{source}</span>
                                <span className="text-white font-medium bg-gray-700/50 px-3 py-1 rounded-full">{count}</span>
                            </div>
                        ))}
                        {Object.keys(stats.activeLeadSources || {}).length === 0 && (
                            <p className="text-sm text-gray-400 text-center py-4">No active lead sources yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Reusable stat card component
const StatCard = ({ title, value, icon, subtitle, trend, trendUp, trendDown }) => (
    <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6 hover:bg-gray-800/80 transition-all duration-200">
        <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-gray-700/30 rounded-lg">
                {icon}
            </div>
            {trend && (
                <span className={`text-sm font-medium px-2 py-1 rounded-md ${trendUp ? 'text-green-400 bg-green-400/10' :
                    trendDown ? 'text-red-400 bg-red-400/10' :
                        'text-gray-400 bg-gray-400/10'
                    }`}>
                    {trend}
                </span>
            )}
        </div>
        <div>
            <h3 className="text-gray-400 text-sm font-medium mb-1">{title}</h3>
            <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
            {subtitle && (
                <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
            )}
        </div>
    </div>
);

export default AdminDashboard;
